/**
 * The five cash flow types (docs/39 §8.2, steward-app#2581). Spread into
 * `STEWARD_APP_TYPE_DEFS` (`index.ts`), so they are seeded into every family workspace by the
 * steward-app pack.
 *
 * ## ARMED (slice 4, steward-app#2583)
 *
 * Each of the five declares the read gate docs/39 §8.2 gives it:
 *
 * | type                  | readCapability             |
 * | --------------------- | -------------------------- |
 * | `transaction`         | `cash-flow:transactions`   |
 * | `transfer-pair`       | `cash-flow:transactions`   |
 * | `cash-flow-month`     | `cash-flow:read`           |
 * | `cash-flow-agreement` | `cash-flow:agreement` (release B, steward-app#2591; write gate `mem:manage`) |
 * | `transaction-cursor`  | `cash-flow-cursor:read` (held by no role; system plane only) |
 *
 * Grant before arm: slice 2 (steward-app#2581, #2601) granted the capabilities to their roles
 * (`packRoles.ts`) and armed nothing, for the two-cache reason in `pillars.ts`'s module
 * docblock. This slice arms the read gates in a later release; the grant's release record is
 * on steward-app#2583 (deployed state is verified at source, never read from this file). The write gates were declared in slice 2:
 * `steward:write` is granted and armed across the pack, and `cash-flow-cursor:write` is held by
 * no role by design. `cashFlowAccess.test.ts` pins each type's `readCapability` exactly and
 * that every one of them is held by a pack role or is on `SYSTEM_ONLY_READ_CAPABILITIES`.
 *
 * What arming means for a member-plane principal (the façade relays every family and firm
 * request): appcore requires the type's `readCapability` on top of `inst:read`, and under
 * BOTH intents, so a write needs it too (`pillars.ts`, "The gate binds writes too"). No pack
 * role other than the four cash flow roles holds either cash flow capability, wildcards
 * included (`cashFlowAccess.test.ts`, "no other pack role reaches"), so a member reaches these
 * rows only through one of those four (two family, two firm). Two principals the
 * gate does not bind, both by appcore's design (docs/39 §8.5): a `*:*` holder, and
 * background actors on `/system/*`, which runs no capability gate. One residual besides
 * those: appcore#313 (open as read 2026-10-27), where a workspace-local CTYPE row shadows the pack type and drops
 * its `accessControl`. `steward-admin` and appcore's `workspace-admin` hold `type:write`, so a
 * family admin could disarm these types in their own workspace; the same admin holds
 * `mem:manage` and could compose the roles anyway, so it is not an escalation, and nothing
 * crosses a workspace. `packRoles.ts` names the same residual for `advisor-client`.
 *
 * ## Access patterns are declared now, on purpose
 *
 * appcore writes a lookup item only when a row is written, for the patterns its type declares
 * at that moment (`ENTITY_LOOKUP#<type>#<pattern>#<value>#<id>`, written only for a
 * single-field pattern whose value is a non-empty string). A pattern declared later would
 * leave every earlier row unfindable through it. So every pattern docs/39 needs is declared
 * before the first row exists, and every pattern field is a STRING (`year` included), because
 * a number value gets no lookup item.
 *
 * ## Schemaless, all five
 *
 * `schemaMode` is immutable after creation, so it is chosen once here. `cash-flow-month`
 * carries nested objects (`byEntity`, `byCategory`) that a strict type cannot declare, and the
 * other four are written by account-sync over `/system/*` or by a façade route that writes a
 * fixed key list, following the `net-worth-snapshot` and `firm-nw-*` precedent. The field
 * lists below document the shape; with `schemaless` appcore does not validate against them.
 * Object-valued fields are declared `text` for the reason `firmNetWorthTypeDefs.ts` gives.
 *
 * None is `versioned`: one row per transaction, pair, month, agreement or cursor, not a
 * history.
 */
import type { FoTypeDef } from './index.js';
import {
  CASH_FLOW_READ_CAPABILITY, CASH_FLOW_TRANSACTIONS_CAPABILITY, CASH_FLOW_AGREEMENT_CAPABILITY,
  CASH_FLOW_CURSOR_READ_CAPABILITY, CASH_FLOW_CURSOR_WRITE_CAPABILITY,
} from './cashFlowAccess.js';
import { STEWARD_WRITE_CAPABILITY as STEWARD_WRITE } from './packRoles.js';

export const CASH_FLOW_TYPE_DEFS: readonly FoTypeDef[] = [
  {
    // One posted transaction from Plaid `/transactions/sync` (docs/39 §8.2). instanceId is
    // account-sync's `syncedInstanceId('transaction', connectionId, plaidTransactionId)`.
    //
    // The `user*` fields are family-owned corrections. The sync writer replaces only the
    // sync-owned keys and carries the `user*` keys over (docs/39 §8.2, "Corrections survive a
    // re-sync"); the façade's PATCH writes only the `user*` keys.
    typeCode: 'transaction',
    name: 'Transaction',
    schemaMode: 'schemaless',
    accessControl: { readCapability: CASH_FLOW_TRANSACTIONS_CAPABILITY, writeCapability: STEWARD_WRITE },
    fields: [
      // sync-owned
      { name: 'connectionId',  type: 'string', widget: 'text',   required: true,  label: 'Connection' },
      { name: 'source',        type: 'string', widget: 'text',   required: true,  label: 'Source (plaid)' },
      { name: 'externalId',    type: 'string', widget: 'text',   required: true,  label: 'Provider transaction id' },
      { name: 'accountId',     type: 'string', widget: 'text',   required: true,  label: 'Account' },
      { name: 'date',          type: 'date',   widget: 'date',   required: true,  label: 'Date (YYYY-MM-DD)' },
      { name: 'month',         type: 'string', widget: 'text',   required: true,  label: 'Month (YYYY-MM)' },
      { name: 'amountCents',   type: 'number', widget: 'number', required: true,  label: 'Amount in cents (Plaid sign: positive is money out)' },
      { name: 'currency',      type: 'string', widget: 'text',   required: false, label: 'Currency (ISO 4217)' },
      { name: 'name',          type: 'string', widget: 'text',   required: false, label: 'Name' },
      { name: 'merchant',      type: 'string', widget: 'text',   required: false, label: 'Merchant' },
      { name: 'pfcPrimary',    type: 'string', widget: 'text',   required: false, label: 'Plaid category (primary)' },
      { name: 'pfcDetailed',   type: 'string', widget: 'text',   required: false, label: 'Plaid category (detailed)' },
      { name: 'pfcConfidence', type: 'string', widget: 'text',   required: false, label: 'Plaid category confidence' },
      // family-owned
      { name: 'userCategory',  type: 'string', widget: 'text',     required: false, label: 'Category (set by the family)' },
      { name: 'userEntityId',  type: 'string', widget: 'text',     required: false, label: 'Entity (set by the family)' },
      { name: 'userFlow',      type: 'string', widget: 'text',     required: false, label: 'Flow (internal, or absent)' },
      { name: 'userNote',      type: 'text',   widget: 'textarea', required: false, label: 'Note' },
    ],
    accessPatterns: [
      { name: 'by_month',      fields: ['month'] },
      { name: 'by_account',    fields: ['accountId'] },
      { name: 'by_connection', fields: ['connectionId'] },
    ],
    presentation: {
      displayField: 'name',
      listColumns: ['date', 'name', 'amountCents', 'accountId'],
      defaultSort: { field: 'date', direction: 'desc' },
    },
  },
  {
    // Two transactions the matcher or the family paired as a move between the family's own
    // accounts (docs/39 §8.2, §8.4). instanceId is sha256 of the two leg ids, sorted.
    typeCode: 'transfer-pair',
    name: 'Transfer Pair',
    schemaMode: 'schemaless',
    accessControl: { readCapability: CASH_FLOW_TRANSACTIONS_CAPABILITY, writeCapability: STEWARD_WRITE },
    fields: [
      { name: 'outTxnId',     type: 'string', widget: 'text',   required: true,  label: 'Outgoing transaction' },
      { name: 'inTxnId',      type: 'string', widget: 'text',   required: true,  label: 'Incoming transaction' },
      { name: 'outMonth',     type: 'string', widget: 'text',   required: true,  label: 'Outgoing month (YYYY-MM)' },
      { name: 'inMonth',      type: 'string', widget: 'text',   required: true,  label: 'Incoming month (YYYY-MM)' },
      { name: 'amountCents',  type: 'number', widget: 'number', required: true,  label: 'Amount in cents' },
      { name: 'currency',     type: 'string', widget: 'text',   required: false, label: 'Currency (ISO 4217)' },
      { name: 'gapDays',      type: 'number', widget: 'number', required: true,  label: 'Days between the legs' },
      { name: 'labelledLegs', type: 'number', widget: 'number', required: true,  label: 'Legs Plaid labelled a transfer (0, 1, 2)' },
      { name: 'status',       type: 'string', widget: 'text',   required: true,  label: 'Status (auto, proposed, confirmed, rejected)' },
      { name: 'decidedBy',    type: 'string', widget: 'text',   required: true,  label: 'Decided by (matcher, family)' },
      { name: 'decidedAt',    type: 'string', widget: 'text',   required: false, label: 'Decided at (ISO timestamp)' },
    ],
    accessPatterns: [
      { name: 'by_out_month', fields: ['outMonth'] },
      { name: 'by_in_month',  fields: ['inMonth'] },
    ],
    presentation: {
      displayField: 'outMonth',
      listColumns: ['outMonth', 'amountCents', 'status'],
      defaultSort: { field: 'outMonth', direction: 'desc' },
    },
  },
  {
    // One month's precomputed totals (docs/39 §8.2, §8.4), so a totals-only viewer reads one
    // small row per month and never needs `cash-flow:transactions`. instanceId is `YYYY-MM`.
    // `byEntity` holds entity ids only, never names.
    typeCode: 'cash-flow-month',
    name: 'Cash Flow Month',
    schemaMode: 'schemaless',
    accessControl: { readCapability: CASH_FLOW_READ_CAPABILITY, writeCapability: STEWARD_WRITE },
    fields: [
      // A string, not a number: appcore writes a lookup item only for a string value.
      { name: 'year',          type: 'string', widget: 'text',     required: true,  label: 'Year (YYYY)' },
      { name: 'inflowCents',   type: 'number', widget: 'number',   required: true,  label: 'Inflows in cents' },
      { name: 'outflowCents',  type: 'number', widget: 'number',   required: true,  label: 'Outflows in cents' },
      { name: 'internalCents', type: 'number', widget: 'number',   required: true,  label: 'Moves between own accounts in cents' },
      { name: 'byEntity',      type: 'text',   widget: 'textarea', required: false, label: 'Totals by entity id ({entityId: {in, out}})' },
      { name: 'byCategory',    type: 'text',   widget: 'textarea', required: false, label: 'Totals by category ({category: {in, out}})' },
      { name: 'openQuestions', type: 'number', widget: 'number',   required: false, label: 'Open questions' },
      // Optional: a row written before this field existed has none, and a reader treats that as unknown.
      { name: 'transactionCount', type: 'number', widget: 'number', required: false, label: 'Transactions dated in the month (0 = nothing there)' },
      { name: 'computedAt',    type: 'string', widget: 'text',     required: true,  label: 'ISO timestamp when computed' },
      // Optional: the included accounts the row was computed from (`includedBasis` in
      // `@steward-app/cash-flow`). The façade shows a row only when it matches the agreement's
      // current list, so a row written before this field existed is treated as not computed.
      { name: 'includedBasis', type: 'string', widget: 'text',     required: false, label: 'Included accounts the totals were computed from' },
    ],
    accessPatterns: [
      { name: 'by_year', fields: ['year'] },
    ],
    presentation: {
      displayField: 'year',
      listColumns: ['year', 'inflowCents', 'outflowCents'],
      defaultSort: { field: 'year', direction: 'desc' },
    },
  },
  {
    // The family's cash flow agreement (docs/39 §7 decision 2, §8.2). A singleton, instanceId
    // `current`. Holds no transaction data.
    //
    // `singletonInstanceId` (crossmarc/appcore#964, steward-app#2792): the façade's
    // `PUT /cash-flow/agreement` creates the row on the first turn-on with the member-plane
    // create on the caller's token, and appcore puts it at `current` instead of a ULID. A
    // second create is 409 `INSTANCE_EXISTS`. Before #2792, account-sync provisioned the row
    // over `/system/*`. appcore refuses changing or removing the declaration on a re-seed
    // through `PackRepository.upsertType`; this repo's raw-Put seed is not held to that, so
    // never change it here.
    //
    // Release B of steward-app#2685 (slice 12, steward-app#2591): read gate `cash-flow:agreement`
    // (held by `steward-admin` and the four cash flow roles, granted in release A), so an admin
    // whose own level is "Nothing" can still read and write it; write gate `mem:manage` (held by
    // `steward-admin`, `workspace-admin`, `tenant-admin` and no advisor-scope role), so a
    // write-capable non-admin cannot change it on the member plane. appcore requires the read
    // capability for a write too, so a writer needs both. The façade's `PUT /cash-flow/agreement`
    // narrows further to `isAdminRoleSet` (`assertWorkspaceAdmin`).
    typeCode: 'cash-flow-agreement',
    name: 'Cash Flow Agreement',
    schemaMode: 'schemaless',
    accessControl: { readCapability: CASH_FLOW_AGREEMENT_CAPABILITY, writeCapability: 'mem:manage' },
    singletonInstanceId: 'current',
    fields: [
      { name: 'acceptedBy',         type: 'string', widget: 'text',     required: true,  label: 'Accepted by (user id)' },
      { name: 'acceptedAt',         type: 'string', widget: 'text',     required: true,  label: 'Accepted at (ISO timestamp)' },
      { name: 'agreementVersion',   type: 'string', widget: 'text',     required: true,  label: 'Agreement version' },
      { name: 'windowMonths',       type: 'number', widget: 'number',   required: true,  label: 'History window in months (3, 12, 24)' },
      { name: 'windowStart',        type: 'date',   widget: 'date',     required: true,  label: 'Window start (YYYY-MM-DD)' },
      // Opt-in (steward-app#2792): only these accounts are read. An account connected later is
      // not in the list until an admin includes it.
      { name: 'includedAccountIds', type: 'array',  widget: 'textarea', required: false, label: 'Accounts included' },
      { name: 'status',             type: 'string', widget: 'text',     required: true,  label: 'Status (on, off)' },
      // Turn-off grace (docs/39 §8.8 Q4, steward-app#2588). The agreement writer (slice 12)
      // stamps `turnedOffAt` when it sets `status: 'off'`; nothing is deleted then. account-sync's
      // nightly sweep purges every transaction, pair, month and cursor row once `turnedOffAt` is
      // seven days old, and stamps `purgedAt` (`services/account-sync/src/cashFlowPurge.ts`). A
      // purge counts for a turn-off only when `purgedAt` is not earlier than `turnedOffAt`, so a
      // later turn-off is purged again. An `off` row without `turnedOffAt` is never purged.
      { name: 'turnedOffAt',        type: 'string', widget: 'text',     required: false, label: 'Turned off at (ISO timestamp)' },
      { name: 'purgedAt',           type: 'string', widget: 'text',     required: false, label: 'Rows purged at (ISO timestamp)' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'status',
      listColumns: ['status', 'windowMonths', 'acceptedAt'],
      defaultSort: { field: 'acceptedAt', direction: 'desc' },
    },
  },
  {
    // account-sync's `/transactions/sync` cursor for one connection (docs/39 §8.2, §8.3).
    // instanceId is the connection id. Read and written only over `/system/*`: the write gate
    // below is held by no role (`SYSTEM_ONLY_WRITE_CAPABILITIES`), and so is the read gate
    // `cash-flow-cursor:read` (`SYSTEM_ONLY_READ_CAPABILITIES`), armed in slice 4. The
    // `firm-nw-*` precedent.
    typeCode: 'transaction-cursor',
    name: 'Transaction Cursor',
    schemaMode: 'schemaless',
    accessControl: { readCapability: CASH_FLOW_CURSOR_READ_CAPABILITY, writeCapability: CASH_FLOW_CURSOR_WRITE_CAPABILITY },
    fields: [
      { name: 'cursor',          type: 'string', widget: 'text', required: false, label: 'Cursor' },
      { name: 'loopStartCursor', type: 'string', widget: 'text', required: false, label: 'Cursor at the start of the loop' },
      { name: 'status',          type: 'string', widget: 'text', required: false, label: 'Status' },
      { name: 'lastSyncAt',      type: 'string', widget: 'text', required: false, label: 'Last sync (ISO timestamp)' },
      { name: 'windowStart',     type: 'date',   widget: 'date', required: false, label: 'Window start (YYYY-MM-DD)' },
      { name: 'earliestDate',    type: 'date',   widget: 'date', required: false, label: 'Oldest date Plaid returned' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'status',
      listColumns: ['status', 'lastSyncAt', 'earliestDate'],
      defaultSort: { field: 'lastSyncAt', direction: 'desc' },
    },
  },
];
